import { PublicRecruitingBrand } from './PublicRecruitingBrand';
import { CandidateAssessment, type PublicAssessmentInfo } from './CandidateAssessment';
// pages/hr/recruiting/PublicApplyPage.tsx
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  FormControl,
  FormLabel,
  Input,
  Checkbox,
  Stack,
  Alert,
  Link,
} from '@mui/joy';
import {
  MapPin as LocationOnOutlined,
  CheckCircle as CheckCircleOutlined,
  FileArrowUp as UploadFileOutlined,
} from '@phosphor-icons/react';
import { useParams } from 'react-router-dom';
import { usePublicCampaign, useSubmitApplication } from '../../../hooks/useHr';
import type {
  ApplicationResponsePayload,
  ApplicationSubmitRequest,
  PublicCampaignInfo,
  QuestionType,
} from '../../../types/hr';

/**
 * The campaign's screening questions, minus the knockout configuration — an
 * applicant must never see which answer disqualifies them.
 */
interface PublicQualificationQuestion {
  id: string;
  question_text: string;
  question_type: QuestionType;
  options?: string[];
  order?: number;
}

interface QuestionFieldProps {
  question: PublicQualificationQuestion;
  value: string;
  onChange: (value: string) => void;
}

/**
 * Screening answers are collected here rather than over SMS, so every control
 * has to be answerable one-handed on a phone: taps for anything with a fixed
 * set of answers, a single short input otherwise.
 */
const QuestionField: React.FC<QuestionFieldProps> = ({ question, value, onChange }) => {
  const choices =
    question.question_type === 'yes_no'
      ? ['Yes', 'No']
      : question.question_type === 'multiple_choice'
        ? question.options || []
        : null;

  return (
    <FormControl required>
      <FormLabel sx={{ alignItems: 'flex-start' }}>{question.question_text}</FormLabel>
      {choices && choices.length > 0 ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {choices.map((choice) => (
            <Button
              key={choice}
              // Buttons inside a form submit it by default; these only pick an answer.
              type="button"
              size="lg"
              variant={value === choice ? 'solid' : 'outlined'}
              color={value === choice ? 'primary' : 'neutral'}
              onClick={() => onChange(choice)}
              sx={{ flex: '1 1 40%', minHeight: 48 }}
            >
              {choice}
            </Button>
          ))}
        </Box>
      ) : (
        <Input
          size="lg"
          type={question.question_type === 'number' ? 'number' : 'text'}
          placeholder="Your answer"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </FormControl>
  );
};

export const ApplicationForm = ({assessmentToken, identity, onSubmitted}: {assessmentToken?: string; identity?: {first_name: string; last_name: string; email: string}; onSubmitted?: () => void}) => {
  const { slug } = useParams<{ slug: string }>();
  const { data: campaign, isLoading, error } = usePublicCampaign(slug || '');
  const submitApplication = useSubmitApplication();

  const [form, setForm] = useState({
    first_name: identity?.first_name || '',
    last_name: identity?.last_name || '',
    email: identity?.email || '',
    phone: '',
    sms_consent: false,
  });
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [resume, setResume] = useState<File | null>(null);
  const [submitted, setSubmitted] = useState(false);

  // Screening questions ride along on the public campaign payload as `questions`.
  // A response without the key just renders the plain application form.
  const questions =
    (campaign as (PublicCampaignInfo & {
      questions?: PublicQualificationQuestion[];
    }) | undefined)?.questions ?? [];

  const unanswered = questions.filter((q) => !(answers[q.id] || '').trim());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!slug) return;

    const responses: ApplicationResponsePayload[] = questions
      .map((q) => ({ question_id: q.id, answer: (answers[q.id] || '').trim() }))
      .filter((r) => r.answer);

    try {
      await submitApplication.mutateAsync({
        slug,
        data: {
          ...form,
          assessment_token: assessmentToken,
          resume: resume || undefined,
          source: 'website',
          // Answers travel with the application so the candidate is scored and
          // qualified immediately instead of waiting on an SMS exchange.
          responses,
        } as ApplicationSubmitRequest & { responses: ApplicationResponsePayload[] },
      });
      if (onSubmitted) onSubmitted(); else setSubmitted(true);
    } catch (err) {
      // Error handled by mutation
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.level1',
        }}
      >
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (error || !campaign) {
    const status = (error as { status?: number } | null)?.status;
    // 404 (or no error but no campaign) = genuinely closed/missing; anything
    // else (500, network) is a transient problem the applicant can retry.
    const isNotFound = !error || status === 404;
    const heading = isNotFound ? 'Position Not Found' : 'Unable to Load This Posting';
    const message = isNotFound
      ? 'This job posting may have been closed or no longer exists.'
      : "We're having trouble loading this job posting right now. Please refresh the page or try again in a few minutes.";

    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.level1',
        }}
      >
        <Card sx={{ maxWidth: 400, textAlign: 'center', p: 4 }}>
          <Typography level="h4" sx={{ mb: 2 }}>
            {heading}
          </Typography>
          <Typography level="body-md" sx={{ color: 'neutral.500' }}>
            {message}
          </Typography>
        </Card>
      </Box>
    );
  }

  if (submitted) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.level1',
          p: 2,
        }}
      >
        <Card sx={{ maxWidth: 500, textAlign: 'center', p: 4 }}>
          <CheckCircleOutlined size={64} color="var(--joy-palette-success-500)" style={{ marginBottom: 16 }} />
          <Typography level="h3" sx={{ mb: 2 }}>
            Application Submitted!
          </Typography>
          <Typography level="body-md" sx={{ color: 'neutral.500' }}>
            Thank you for applying to the {campaign.role_title} position at{' '}
            {campaign.organization_name}. We will contact you soon via SMS to continue the
            application process.
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.level1',
        py: 4,
        px: 2,
      }}
    >
      <Box sx={{ maxWidth: 600, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          {campaign.organization_logo && (
            <Box
              component="img"
              src={campaign.organization_logo}
              alt={campaign.organization_name}
              sx={{ height: 60, mb: 2 }}
            />
          )}
          <Typography level="h2" sx={{ mb: 1 }}>
            {campaign.role_title}
          </Typography>
          <Typography level="body-lg" sx={{ color: 'neutral.600' }}>
            {campaign.organization_name}
          </Typography>
          {campaign.location && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                mt: 1,
              }}
            >
              <LocationOnOutlined size={18} color="var(--joy-palette-neutral-500)" />
              <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                {campaign.location}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Job Description */}
        {campaign.description && (
          <Card sx={{ mb: 3 }}>
            <Typography level="title-lg" sx={{ mb: 1 }}>
              About This Role
            </Typography>
            <Typography level="body-md" sx={{ whiteSpace: 'pre-wrap' }}>
              {campaign.description}
            </Typography>
          </Card>
        )}

        {/* Application Form */}
        <Card>
          <Typography level="title-lg" sx={{ mb: 2 }}>
            {assessmentToken ? "Your application details" : "Apply Now"}
          </Typography>

          {submitApplication.isError && (
            <Alert color="danger" sx={{ mb: 2 }}>
              {submitApplication.error?.message || 'Failed to submit application. Please try again.'}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl sx={{ flex: 1 }} required>
                  <FormLabel>First Name</FormLabel>
                  <Input
                    placeholder="John"
                    value={form.first_name}
                    onChange={(e) =>
                      setForm({ ...form, first_name: e.target.value })
                    }
                  />
                </FormControl>
                <FormControl sx={{ flex: 1 }} required>
                  <FormLabel>Last Name</FormLabel>
                  <Input
                    placeholder="Doe"
                    value={form.last_name}
                    onChange={(e) =>
                      setForm({ ...form, last_name: e.target.value })
                    }
                  />
                </FormControl>
              </Box>

              <FormControl required>
                <FormLabel>Email Address</FormLabel>
                <Input
                  type="email"
                  placeholder="john@example.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </FormControl>

              <FormControl required>
                <FormLabel>Phone Number</FormLabel>
                <Input
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </FormControl>

              {questions.length > 0 && (
                <Box sx={{ pt: 1 }}>
                  <Typography level="title-sm" sx={{ mb: 0.5 }}>
                    A Few Quick Questions
                  </Typography>
                  <Typography level="body-xs" sx={{ color: 'neutral.500', mb: 2 }}>
                    Takes under a minute. Answering now means we can call you back sooner.
                  </Typography>
                  <Stack spacing={2}>
                    {questions.map((question) => (
                      <QuestionField
                        key={question.id}
                        question={question}
                        value={answers[question.id] || ''}
                        onChange={(value) =>
                          setAnswers((prev) => ({ ...prev, [question.id]: value }))
                        }
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              <FormControl>
                <FormLabel>Resume (Optional)</FormLabel>
                <Button
                  component="label"
                  variant="outlined"
                  color="primary"
                  startDecorator={<UploadFileOutlined />}
                  sx={{ justifyContent: 'flex-start' }}
                >
                  {resume ? resume.name : 'Upload Resume (PDF, DOC, DOCX)'}
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    hidden
                    onChange={(e) =>
                      setResume(e.target.files?.[0] || null)
                    }
                  />
                </Button>
              </FormControl>

              <FormControl
                sx={{
                  p: 1.5,
                  borderRadius: 'md',
                  border: '1px solid',
                  borderColor: form.sms_consent ? 'primary.400' : 'neutral.outlinedBorder',
                }}
              >
                <Checkbox
                  size="lg"
                  label={
                    <Typography level="body-sm">
                      I agree to receive job application and interview texts from {campaign.organization_name}.
                      Frequency varies. Message and data rates may apply. Reply STOP to opt out or HELP for help.
                      Text consent is optional and is not a condition of applying or employment.
                    </Typography>
                  }
                  checked={form.sms_consent}
                  onChange={(e) =>
                    setForm({ ...form, sms_consent: e.target.checked })
                  }
                />
              </FormControl>

              <Stack direction="row" spacing={2}>
                {campaign.texting_privacy_url && <Link href={campaign.texting_privacy_url} target="_blank" rel="noopener noreferrer">Texting privacy policy</Link>}
                {campaign.texting_terms_url && <Link href={campaign.texting_terms_url} target="_blank" rel="noopener noreferrer">Texting terms</Link>}
              </Stack>

              <Button
                type="submit"
                size="lg"
                loading={submitApplication.isPending}
                disabled={
                  !form.first_name ||
                  !form.last_name ||
                  !form.email ||
                  !form.phone ||
                  unanswered.length > 0
                }
              >
                {assessmentToken ? "Continue to assessment" : "Submit Application"}
              </Button>

              {/* A disabled button with no explanation reads as a broken page. */}
              {unanswered.length > 0 && (
                <Typography level="body-xs" sx={{ textAlign: 'center', color: 'neutral.500' }}>
                  {unanswered.length === 1
                    ? '1 question still needs an answer.'
                    : `${unanswered.length} questions still need an answer.`}
                </Typography>
              )}
              {unanswered.length === 0 && !form.sms_consent && (
                <Typography level="body-xs" sx={{ textAlign: 'center', color: 'neutral.500' }}>
                  You can apply without text messages. The hiring team can contact you by email.
                </Typography>
              )}

              <Typography level="body-xs" sx={{ textAlign: 'center', color: 'neutral.500' }}>
                By submitting this application, you agree to allow{' '}
                {campaign.organization_name} to process your information for hiring purposes.
              </Typography>
            </Stack>
          </form>
        </Card>
      </Box>
    </Box>
  );
};

export const PublicApplyPage = () => {
  const { slug = '' } = useParams<{slug: string}>();
  const { data: campaign } = usePublicCampaign(slug);
  const configuredAssessment = (campaign as (PublicCampaignInfo & {assessment?: PublicAssessmentInfo}) | undefined)?.assessment;
  const hasSession = !!(new URLSearchParams(window.location.hash.slice(1)).get('assessment') || sessionStorage.getItem(`assessment:${slug}`));
  const assessment = configuredAssessment || (hasSession ? {title: 'Resume assessment', welcome: '', estimated_minutes: 25, settings: {}} : undefined);
  if (campaign && assessment) return <PublicRecruitingBrand campaign={campaign}><CandidateAssessment slug={slug} campaign={campaign} assessment={assessment} renderApplication={(token, identity, onSubmitted) => <ApplicationForm assessmentToken={token} identity={identity} onSubmitted={onSubmitted} />} /></PublicRecruitingBrand>;
  return <PublicRecruitingBrand campaign={campaign}><ApplicationForm /></PublicRecruitingBrand>;
};
export default PublicApplyPage;
